/*************************************************************************
 * HeaderOptions.jsx  –  immediate updates, no missing text
 *************************************************************************/
import React, { memo, useState, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as NoneIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Description as DocumentIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import IconRadioSelect from "./IconRadioSelect";
import TextFieldWithVariableButton from "./TextFieldVariableButton";
import { API_ENDPOINT } from "../../utils/constants";
import { getMultipartHeader } from "../../utils/util";

/* ─────────────── UI helpers ─────────────── */
const optionsList = [
  { value: "NONE", label: "None", icon: <NoneIcon /> },
  { value: "TEXT", label: "Text", icon: <TextIcon /> },
  { value: "IMAGE", label: "Image", icon: <ImageIcon /> },
  { value: "VIDEO", label: "Video", icon: <VideoIcon /> },
  { value: "DOCUMENT", label: "Document", icon: <DocumentIcon /> },
  { value: "LOCATION", label: "Location", icon: <LocationIcon /> },
];
/* identical to MUI's useControlled */
function useControlled({ controlled, defaultProp }) {
  const [state, setState] = useState(defaultProp);
  const isCtrl = controlled !== undefined;
  return [isCtrl ? controlled : state, isCtrl ? () => {} : setState];
}

/* ────────────────── COMPONENT ────────────────── */
function HeaderOptions({
  value: valueProp,
  defaultValue = { type: "NONE" },
  onChange,
  onMediaUrl,
  variableType = "Number",
}) {
  /* ── controlled / uncontrolled glue ── */
  const [value, setValueUnctrl] = useControlled({
    controlled: valueProp,
    defaultProp: defaultValue,
  });

  /** write helper – always informs parent; updates local when uncontrolled */
  const emit = useCallback(
    (next) => {
      onChange?.(next);
      setValueUnctrl(next);
    },
    [onChange, setValueUnctrl]
  );

  /* ── upload UI state ── */
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState(null); // success / error message
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetUploadUi = () => {
    setFile(null);
    setMsg(null);
    setErr(false);
    setLoading(false);
  };

  /* ── handlers ── */
  const handleType = (e) => {
    resetUploadUi();
    emit({ type: e.target.value, text: "", mediaId: "" });
  };

  /** normalise value from either event or raw string */
  const extractVal = (v) =>
    typeof v === "string" ? v : v?.target?.value || "";

  /* ── render ── */
  const { type = "NONE", text = "", mediaId = "" } = value;

  return (
    <Box>
      <IconRadioSelect
        options={optionsList}
        label="Content Type"
        size="small"
        value={type}
        onChange={handleType}
        sx={{ mb: 2 }}
      />

      {/* TEXT header */}
      {type === "TEXT" && (
        <Box sx={{ mt: 1 }}>
          <TextFieldWithVariableButton
            value={text}
            onChange={(newText, param) => {
              emit({ ...value, text: newText, param }); // store both if you like
            }}
            variableType={variableType}
            varLimitCount={1}
            charLimit={60}
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(HeaderOptions);
